import jsPDF from "jspdf";
import html2canvas from "html2canvas-pro";
import { Block } from "../types/screenplay-editor.types";
import { GUJARATI_FONT_BASE64 } from "../utils/gujarati-font";
import { GUJARATI_FONT_BOLD_BASE64 } from "../utils/gujarati-font-bold";
import { HINDI_FONT_BASE64 } from "../utils/hindi-font";
import { HINDI_FONT_BOLD_BASE64 } from "../utils/hindi-font-bold";

export const handleSaveAsPdf = async (
  blocks: Block[],
  sceneNumbers: Record<string, string>,
  project_name?: string,
) => {
  // 1. Blur active elements
  if (document.activeElement instanceof HTMLElement) {
    document.activeElement.blur();
  }

  // 2. Initialize PDF
  const doc = new jsPDF({
    orientation: "portrait",
    unit: "mm",
    format: "letter",
  });

  // Register Gujarati Fonts
  doc.addFileToVFS("NotoSansGujarati-Regular.ttf", GUJARATI_FONT_BASE64);
  doc.addFont("NotoSansGujarati-Regular.ttf", "NotoSansGujarati", "normal");
  doc.addFileToVFS("NotoSansGujarati-Bold.ttf", GUJARATI_FONT_BOLD_BASE64);
  doc.addFont("NotoSansGujarati-Bold.ttf", "NotoSansGujarati", "bold");

  // Register Hindi Fonts
  doc.addFileToVFS("NotoSansDevanagari-Regular.ttf", HINDI_FONT_BASE64);
  doc.addFont("NotoSansDevanagari-Regular.ttf", "NotoSansDevanagari", "normal");
  doc.addFileToVFS("NotoSansDevanagari-Bold.ttf", HINDI_FONT_BOLD_BASE64);
  doc.addFont("NotoSansDevanagari-Bold.ttf", "NotoSansDevanagari", "bold");

  // Set default font
  doc.setFont("Courier", "normal");

  const FONT_SIZE = 12;
  const LINE_HEIGHT = 6;
  const PAGE_WIDTH = doc.internal.pageSize.getWidth();
  const PAGE_HEIGHT = doc.internal.pageSize.getHeight();

  const MARGIN_LEFT = 38.1;
  const MARGIN_RIGHT = 25.4;
  const MARGIN_TOP = 25.4;
  const MARGIN_BOTTOM = 25.4;

  const blockDimensions: Record<string, any> = {
    SCENE_HEADING: { indent: 0, width: 152.4, upper: true, bold: true },
    ACTION: { indent: 0, width: 152.4, upper: false, bold: false },
    CHARACTER: { indent: 50.8, width: 101.6, upper: true, bold: false },
    PARENTHETICAL: { indent: 38.1, width: 50.8, upper: false, bold: false },
    DIALOGUE: { indent: 25.4, width: 88.9, upper: false, bold: false },
    TRANSITION: {
      indent: 0,
      width: 152.4,
      upper: true,
      bold: false,
      align: "right",
    },
    GENERAL: { indent: 0, width: 152.4, upper: false, bold: false },
  };

  let y = MARGIN_TOP;
  let pageNumber = 1;

  const drawPageNumber = (num: number) => {
    if (num > 1) {
      doc.setFont("Courier", "normal");
      doc.setFontSize(12);
      doc.text(`${num}.`, PAGE_WIDTH - MARGIN_RIGHT, 12.7, {
        align: "right",
      });
    }
  };

  // 4. Processing Blocks
  for (const block of blocks as any[]) {
    const config = blockDimensions[block.type] || blockDimensions.GENERAL;
    let text = block.text || "";

    if (config.upper) text = text.toUpperCase();
    if (block.type === "SCENE_HEADING") {
      text =
        `${block.sceneType || "INT."} ${text} - ${block.timeOfDay || "DAY"}`.toUpperCase();
    }

    const hasGujarati = /[\u0A80-\u0AFF]/.test(text);
    const hasHindi = /[\u0900-\u097F]/.test(text);
    const isIndic = hasGujarati || hasHindi;

    if (hasGujarati) {
      doc.setFont("NotoSansGujarati", config.bold ? "bold" : "normal");
    } else if (hasHindi) {
      doc.setFont("NotoSansDevanagari", config.bold ? "bold" : "normal");
    } else {
      doc.setFont("Courier", config.bold ? "bold" : "normal");
    }
    doc.setFontSize(FONT_SIZE);

    // --- NEW RENDERING LOGIC ---
    let blockHeight = 0;
    const xPos = MARGIN_LEFT + config.indent;

    if (isIndic) {
      // Create temporary element for perfect browser-based shaping
      const tempDiv = document.createElement("div");
      tempDiv.style.width = `${config.width}mm`;

      if (hasGujarati) {
        tempDiv.style.fontFamily = "NotoSansGujarati, Arial, sans-serif";
      } else {
        tempDiv.style.fontFamily = "NotoSansDevanagari, Arial, sans-serif";
      }
      tempDiv.style.fontSize = `${FONT_SIZE}pt`;
      tempDiv.style.lineHeight = `${LINE_HEIGHT}mm`;
      tempDiv.style.whiteSpace = "pre-wrap";
      tempDiv.style.position = "fixed";
      tempDiv.style.left = "-9999px";
      tempDiv.style.top = "0";
      tempDiv.style.color = "black";
      tempDiv.style.padding = "0";
      tempDiv.style.margin = "0";
      if (config.bold) tempDiv.style.fontWeight = "bold";
      if (config.align === "right") tempDiv.style.textAlign = "right";
      tempDiv.innerText = text;
      document.body.appendChild(tempDiv);

      const canvas = await html2canvas(tempDiv, {
        backgroundColor: null,
        scale: 4, // High resolution
        logging: false,
      });
      const imgData = canvas.toDataURL("image/png");
      const imgWidth = config.width;
      const imgHeight = (canvas.height / canvas.width) * imgWidth;

      blockHeight = imgHeight;

      // Overflow check for images
      if (y + blockHeight > PAGE_HEIGHT - MARGIN_BOTTOM) {
        doc.addPage();
        pageNumber++;
        drawPageNumber(pageNumber);
        y = MARGIN_TOP;
      }

      // Vertical Spacing (Standard screenplay spacing)
      if (y > MARGIN_TOP) {
        if (
          block.type === "SCENE_HEADING" ||
          block.type === "ACTION" ||
          block.type === "CHARACTER"
        ) {
          y += LINE_HEIGHT;
        }
      }

      // --- SEARCHABLE LAYER ---
      // We add the text using 'invisible' rendering mode so it is extractable/searchable
      // without drawing any pixels that might overlap or peak through the images.
      const searchLines = doc.splitTextToSize(text, config.width);
      doc.text(searchLines, xPos, y, { renderingMode: "invisible" });

      doc.addImage(imgData, "PNG", xPos, y, imgWidth, imgHeight);
      document.body.removeChild(tempDiv);
    } else {
      // Standard English rendering (Fast & Searchable)
      const lines = doc.splitTextToSize(text, config.width);
      blockHeight = lines.length * LINE_HEIGHT;

      // Overflow check for text
      let safetyBuffer = 0;
      if (block.type === "CHARACTER") safetyBuffer = LINE_HEIGHT * 3;
      if (y + blockHeight + safetyBuffer > PAGE_HEIGHT - MARGIN_BOTTOM) {
        doc.addPage();
        pageNumber++;
        drawPageNumber(pageNumber);
        y = MARGIN_TOP;
        doc.setFont("Courier", config.bold ? "bold" : "normal");
        doc.setFontSize(FONT_SIZE);
      }

      // Vertical Spacing
      if (y > MARGIN_TOP) {
        if (
          block.type === "SCENE_HEADING" ||
          block.type === "ACTION" ||
          block.type === "CHARACTER"
        ) {
          y += LINE_HEIGHT;
        }
      }

      if (config.align === "right") {
        doc.text(lines, PAGE_WIDTH - MARGIN_RIGHT, y, { align: "right" });
      } else {
        doc.text(lines, xPos, y);
      }
    }

    // Scene Numbers (Always Courier/Searchable)
    if (block.type === "SCENE_HEADING" && sceneNumbers?.[block.id]) {
      const sNum = String(sceneNumbers[block.id]);
      doc.setFont("Courier", "normal");
      doc.setFontSize(FONT_SIZE);
      doc.text(sNum, MARGIN_LEFT - 12, y);
      doc.text(sNum, PAGE_WIDTH - MARGIN_RIGHT + 5, y);
    }

    y += blockHeight;
  }

  if (project_name) {
    doc.save(`${project_name}_${new Date().toISOString()}.pdf`);
  } else {
    doc.save("screenplay_export.pdf");
  }
};

export const convertBlocksToSbx = (
  blocks: Block[],
  sceneNumbers: Record<string, string>,
): string => {
  const typeToDivClass: Record<string, string> = {
    SCENE_HEADING: "divtype0",
    ACTION: "divtype2",
    CHARACTER: "divtype3",
    PARENTHETICAL: "divtype4",
    DIALOGUE: "divtype5",
    TRANSITION: "divtype6",
    GENERAL: "divtype2",
  };

  return blocks
    .map((block: any) => {
      const divClass = typeToDivClass[block.type] || "divtype2";
      let text = block.text || "";
      let extraAttributes = "";

      if (block.type === "SCENE_HEADING") {
        text =
          `${block.sceneType || "INT."} ${text} - ${block.timeOfDay || "DAY"}`.toUpperCase();
        const sceneNum = sceneNumbers?.[block.id];
        if (sceneNum) {
          extraAttributes = ` data-scene="${sceneNum}"`;
        }
      } else if (block.type === "CHARACTER" || block.type === "TRANSITION") {
        text = text.toUpperCase();
      }

      return `<div class="${divClass}" id="par${block.id}"${extraAttributes}>${text}</div>`;
    })
    .join("");
};

export const handleSyncWithCloud = (
  blocks: Block[],
  sceneNumbers: Record<string, string>,
  onSaveAsSbx?: (file: File) => void,
  project_name?: string,
) => {
  const sbxData = convertBlocksToSbx(blocks, sceneNumbers);

  const blob = new Blob([sbxData], { type: "text/plain" });

  // 1. Trigger a local download of the .sbx file
  // const url = URL.createObjectURL(blob);
  // const a = document.createElement("a");
  // a.href = url;
  // a.download = "screenplay.sbx";
  // document.body.appendChild(a);
  // a.click();
  // document.body.removeChild(a);
  // URL.revokeObjectURL(url);

  // 2. Pass the binary file to the API function provided via props
  if (onSaveAsSbx) {
    if (project_name) {
      const file = new File(
        [blob],
        `${project_name}_${new Date().toISOString()}.sbx`,
        { type: "text/plain" },
      );
      onSaveAsSbx(file);
    } else {
      const file = new File([blob], "screenplay.sbx", { type: "text/plain" });
      onSaveAsSbx(file);
    }
  }
};
